/* eslint-disable @typescript-eslint/naming-convention */
import type React from 'react';
import { useEffect } from 'react';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { login as loginRoute } from 'src/app/onboarding/routes/routes';
import { useIdentity } from 'src/hooks/useIdentity';
import { useIsCheckedRoute } from 'src/hooks/useIsCheckedRoute';
import { shouldOptOutOfAmplitude } from 'src/lib/analytics/amplitude/helpers/shouldOptOutOfAmplitude';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { Config } from 'src/lib/config/config';
import { isCypressUserEmail } from 'src/lib/isCypressUser';
import { useLDFlag } from 'src/lib/launchDarkly';

type amplitudeFlagConfig = {
  trackingEnabled: boolean;
  trackEmbeddedUsers: boolean;
};

type ReactChildren = {
  children: React.ReactElement;
};

const useAmplitudeConfig = (): amplitudeFlagConfig => {
  const trackingEnabled = useLDFlag('studio-amplitude-tracking-enabled');

  const trackEmbeddedUsers = useLDFlag('betelgeuse-amplitude-in-embedded');

  return {
    trackingEnabled,
    trackEmbeddedUsers,
  };
};

export const AmplitudeProvider = ({ children }: ReactChildren) => {
  const { me, isUser, myAccount, memberships } = useIdentity();
  const graphRef = useGraphRef();

  const amplitudeConfiguration = useAmplitudeConfig();
  const excludeRouteFromTracking =
    useIsCheckedRoute(loginRoute.definition) || isEmbeddableExplorerRoute();

  useEffect(() => {
    const shouldOptOut = shouldOptOutOfAmplitude({
      excludeRouteFromTracking,
      amplitudeConfiguration,
    });
    ampli.client.setOptOut(shouldOptOut);
  }, [excludeRouteFromTracking, amplitudeConfiguration]);

  useEffect(() => {
    // Call ampli.identify when `me` or `isUser` is updated
    if (me && isUser && ampli.isLoaded) {
      // Find the user's role for the current active account
      const userRole = memberships.find(
        (membership) => membership.account.id === myAccount?.id,
      )?.role;

      ampli.identify(
        me.id,
        // identify event properties
        {
          'Active Org Id': myAccount?.internalID,
          active_org_subscription_tier: myAccount?.currentPlan?.tier,
          'Email Verified': me.emailVerified,
          Email: me?.email || undefined,
          'Full Name': me.fullName,
          user_role: userRole,
        },
        // options
        {
          app_version: Config.gitCommitHash,
          user_id: me.id,
        },
      );
    }
  }, [
    me,
    isUser,
    graphRef,
    myAccount?.internalID,
    myAccount?.id,
    myAccount?.currentPlan?.tier,
    memberships,
  ]);

  // Remove Amplitude Session Replay plugin for Cypress test users after authentication
  useEffect(() => {
    if (me && isUser && ampli.isLoaded) {
      const userEmail = me.email;
      if (userEmail && isCypressUserEmail(userEmail)) {
        ampli.client.remove('sessionReplayTracking');
      }
    }
  }, [me, isUser]);

  return children;
};
